import { of, combineLatest } from 'rxjs'
import {
  switchMap,
  tap,
  withLatestFrom,
  ignoreElements,
  filter,
  map
} from 'rxjs/operators'
import Actions from '../actions'
import { getPayload } from '../utils/rxUtils'
import { event, eventTypes } from '../consts/eventConsts'
import {
  formTrackingEventHitscope,
  pageViewEventPageScope,
  pageViewEventUserScope
} from '../utils/eventUtils'
import LogRocket from 'logrocket'
import { getAge } from '../utils/calcUtils'
import { getOwnerMember } from '../utils/memberUtils'
import routes from 'consts/routes'

const dataLayerPush = event => {
  window.dataLayer = window.dataLayer || []
  event.event = 'data.' + event.event
  window.dataLayer.push(event)
}

const analyticsHandler = (
  type,
  event,
  element = null,
  data = null,
  error = null,
  userScope = null
) => {
  let dataLayerEvent = { event: event }
  let logrocketData = { ...data }
  if (type === eventTypes.formEvent || type === eventTypes.botEvent) {
    dataLayerEvent = {
      ...dataLayerEvent,
      ...formTrackingEventHitscope(element, data, error)
    }
    logrocketData = {
      ...logrocketData,
      ...formTrackingEventHitscope(element, data, error)
    }
    if (userScope) {
      dataLayerEvent = { ...dataLayerEvent, ...userScope }
    }
  } else if (type === eventTypes.pageViewEvent) {
    dataLayerEvent = {
      ...dataLayerEvent,
      ...pageViewEventPageScope(data),
      ...pageViewEventUserScope(data)
    }
    logrocketData = {
      ...logrocketData,
      ...pageViewEventPageScope(data),
      ...pageViewEventUserScope(data)
    }
  } else if (type === eventTypes.purchaseEvent) {
    dataLayerEvent = {
      ...dataLayerEvent,
      ...data
    }
  } else if (type === eventTypes.click) {
    dataLayerEvent = {
      ...dataLayerEvent,
      ...data
    }
  }
  dataLayerPush(dataLayerEvent)
  LogRocket.track(event, logrocketData)
}

export const initAnalytics = action$ =>
  action$.pipe(
    getPayload(Actions.APP_LAUNCHED),
    switchMap(() => of(Actions.createAction(Actions.ANALYTICS_INITIATED, {})))
  )

// Log Events:

const formatAnalyticMessage = eventName => {
  return eventName.toLowerCase()
}

export const genericAnalyticRecord = (action$, state$) => {
  return combineLatest([
    action$.pipe(getPayload(Actions.ANALYTICS_EVENT_FIRED)),
    action$.pipe(getPayload(Actions.ANALYTICS_INITIATED))
  ]).pipe(
    withLatestFrom(state$),
    tap(([[{ type, eventName, element, data, error, userScope }], state]) => {
      analyticsHandler(
        type,
        formatAnalyticMessage(eventName),
        element,
        data,
        error,
        userScope
      )
    }),
    ignoreElements()
  )
}

// analytics for page view
export const PageViewAnalytics = (action$, state$) => {
  return action$.pipe(
    getPayload(Actions.LOCATION_CHANGED),
    withLatestFrom(state$),
    switchMap(([{ location }, { member, application, locale, broker }]) => {
      const ownerMember = getOwnerMember(member.members)
      return of(
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.pageViewEvent,
          eventName: event.pageViewEvent.page_view,
          data: {
            url: `${window.location?.protocol}//${window.location?.host}${location?.pathname}`,
            template: Object.keys(routes).find(
              key => routes[key] === location.pathname
            ),
            title: window.document?.title,
            first_name: ownerMember?.firstName,
            last_name: ownerMember?.lastName,
            user_email: ownerMember?.email,
            user_phone_number: ownerMember?.phone,
            user_id: application?.applicationId,
            gender: ownerMember?.gender,
            age: ownerMember?.dob ? getAge(ownerMember.dob) : undefined,
            locale: locale?.locale,
            broker_id: broker?.npn,
          }
        })
      )
    })
  )
}

// analytics for step completed with data for every step navigation
export const navNextStepAnalytics = action$ =>
  action$.pipe(
    getPayload(Actions.STEP_SAVED),
    // don't fire analytics event for steps that dont require navigation
    filter(({ skipNavigation }) => !skipNavigation),
    map(({ step, data }) =>
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.formEvent,
        eventName: event.formEvent.submit,
        data,
        element: step
      })
    )
  )

export const saveProgressEmailAnalytics = action$ =>
  action$.pipe(
    getPayload(Actions.SAVE_PROGRESS_RECEIVED),
    filter(payload => !!payload?.saveProgressEmailRequested),
    map(() =>
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.formEvent,
        eventName: event.formEvent.submit,
        data: {},
        element: 'save progress'
      })
    )
  )

export const requestedErrorAnalytics = action$ =>
  action$.pipe(
    getPayload(Actions.OPERATION_FAILED),
    map(({ error }) =>
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.formEvent,
        eventName: event.formEvent.invalid,
        error: error?.body?.detail,
        element: error?.status?.toString()
      })
    )
  )

const salesforceAnalyticsHandler = () => {
  window.embedded_svc.addEventHandler('onHelpButtonClick', function (data) {
    analyticsHandler(
      eventTypes.formEvent,
      formatAnalyticMessage(event.formEvent.submit),
      'onHelpButtonClick',
      data
    )
  })

  window.embedded_svc.addEventHandler('onChasitorMessage', function (data) {
    analyticsHandler(
      eventTypes.formEvent,
      formatAnalyticMessage(event.formEvent.submit),
      'onChasitorMessage',
      data
    )
  })

  window.embedded_svc.addEventHandler('onChatRequestSuccess', function (data) {
    analyticsHandler(
      eventTypes.formEvent,
      formatAnalyticMessage(event.formEvent.submit),
      'onChatRequestSuccess',
      data
    )
  })
}

export const salesforceAnalyticsInit = action$ => {
  return action$.pipe(getPayload(Actions.ANALYTICS_INITIATED)).pipe(
    tap(() => {
      salesforceAnalyticsHandler()
    }),
    ignoreElements()
  )
}
